// SPDX-License-Identifier: AGPL-3.0-or-later

// KONVO: the personal WhatsApp bridge, client side. Link status drives
// whether the WhatsApp page, sidebar button and Rail tile exist at all.
// Chats and messages load from the API once, then live gateway events from
// the bridge (new messages, ticks, reactions, edits, deletes, typing) are
// applied in place, the way WhatsApp Web keeps its view current. Sending is
// optimistic: the bubble appears with a clock and the ticks catch up.

import {Routes} from '@app/app/Routes';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import type {Gif} from '@app/features/expressions/commands/GifCommands';
import KonvoNotificationPrefs from '@app/features/konvo_notifications/state/KonvoNotificationPrefs';
import {offerInAppPopup} from '@app/features/konvo_popups/state/InAppPopupBridge';
import * as NotificationUtils from '@app/features/notification/utils/NotificationUtils';
import {CoalescedLoad} from '@app/features/platform/transport/CoalescedLoad';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import LocalPresence from '@app/features/presence/state/LocalPresence';
import NotificationState from '@app/features/ui/state/Notification';
import Window from '@app/features/window/state/Window';
import {generateUuid} from '@app/lib/Uuid';
import {StatusTypes} from '@konvo/constants/src/StatusConstants';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

export type WhatsAppLinkState = 'unavailable' | 'unlinked' | 'pairing' | 'connecting' | 'connected' | 'closed';

export interface WhatsAppStatus {
	available: boolean;
	linked: boolean;
	state: WhatsAppLinkState;
	qr: string | null;
	phone: string | null;
	display_name: string | null;
	last_synced_at?: string | null;
	diagnostic?: string | null;
	phone_connected?: boolean | null;
}

export interface WhatsAppChat {
	jid: string;
	name: string;
	avatar_url: string | null;
	has_picture: boolean;
	is_group: boolean;
	unread: number;
	last_at: string | null;
	last_preview: string | null;
	/** The newest message's media kind when the list draws an icon for it, its sender, and a voice note's seconds. */
	last_kind?: string | null;
	last_sender?: string | null;
	last_seconds?: number | null;
	/** The newest message is yours, and its ticks; older bridges send neither. */
	last_from_me?: boolean;
	last_status?: WhatsAppMessageStatus | null;
	archived: boolean;
	pinned: boolean;
	mute_until?: number | null;
	member_count: number | null;
}

export type WhatsAppMessageStatus = 'pending' | 'sent' | 'delivered' | 'read' | 'failed';

export interface WhatsAppReceiptCounts {
	read: number;
	delivered: number;
	/** The other members of the group; null when WhatsApp did not say. */
	total: number | null;
}

/** Message info for one of our messages: who read it, who has it, who is still waiting. */
export interface WhatsAppReceipts {
	group: boolean;
	total: number | null;
	read: Array<{jid: string; name: string; at: string | null}>;
	delivered: Array<{jid: string; name: string; at: string | null}>;
	waiting: Array<{jid: string; name: string}>;
	/** The earliest receipt Konvo kept for this chat; older messages have no per-person receipts. */
	tracked_since: string | null;
}

export interface WhatsAppPollInput {
	question: string;
	options: Array<string>;
	multiple: boolean;
}
export interface WhatsAppPoll {
	question: string;
	selectable_count: number;
	can_vote: boolean;
	total_voters: number;
	options: Array<{
		name: string;
		selected: boolean;
		voters: Array<{jid: string; name: string | null; from_me: boolean}>;
	}>;
}

export interface WhatsAppLocationInput {
	latitude: number;
	longitude: number;
	name: string | null;
	address: string | null;
}
export interface WhatsAppLocation extends WhatsAppLocationInput {
	url: string | null;
	live: boolean;
	has_thumbnail: boolean;
}
export interface WhatsAppContactInput {
	name: string;
	/** Digits with the country code. */
	phone: string;
}
export interface WhatsAppContactCard {
	name: string;
	phones: Array<{number: string; jid: string | null}>;
}
/** Someone in the phone's address book, for sharing as a contact card. */
export interface WhatsAppAddressBookEntry {
	jid: string;
	name: string;
	phone: string;
	has_picture: boolean;
}

export interface WhatsAppMessage {
	poll?: WhatsAppPoll | null;
	local_poll?: WhatsAppPollInput;
	location?: WhatsAppLocation | null;
	local_location?: WhatsAppLocationInput;
	contacts?: Array<WhatsAppContactCard> | null;
	local_contacts?: Array<WhatsAppContactInput>;

	transcript?: string | null;
	id: string;
	jid: string;
	from_me: boolean;
	sender_jid: string | null;
	sender_name: string | null;
	text: string;
	/** image | video | audio | document | sticker | deleted | null */
	media_kind: string | null;
	media_name: string | null;
	/** Photo or video size, to reserve space before it loads. */
	media_width: number | null;
	media_height: number | null;
	/** A GIF: WhatsApp sends it as a silent clip (media_kind video) that loops in the bubble. */
	media_gif?: boolean;
	/** A clip's length in whole seconds, when WhatsApp sent it. */
	media_seconds?: number | null;
	/** Document size in bytes, page count and MIME type, for the file card. */
	media_size?: number | null;
	media_pages?: number | null;
	media_mime?: string | null;
	/** A voice note's length in whole seconds and its 0–100 waveform. */
	voice_seconds?: number | null;
	voice_waveform?: Array<number> | null;
	/** Ticks for our own messages. */
	status: WhatsAppMessageStatus | null;
	/** Our own messages in a group: how many members have read it and got it (read counts as got), of how many. */
	receipts?: WhatsAppReceiptCounts | null;
	sent_at: string;
	reply_to: string | null;
	quote_text: string | null;
	quote_sender: string | null;
	reactions: Array<{emoji: string; from_me: boolean; sender_name: string | null}>;
	/** "@digits" in the text → the person's name. */
	mentions: Record<string, string>;
	sender_has_picture: boolean;
	/** Phone jid to fetch the sender's picture with (senders may be stored under a @lid). */
	sender_avatar_jid: string | null;
	/** Changed after it was sent. */
	edited?: boolean;
	/** Not on WhatsApp yet: an optimistic bubble waiting for the bridge. */
	local?: boolean;
	/** Reserved WhatsApp ID, reused for every retry of this bubble. */
	send_id?: string;
	/** Object URL of a photo being sent, shown until the real one comes back. */
	local_url?: string | null;
	/** What to resend when a local message failed. */
	local_file?: File | null;
	/** The local file is a composer recording, to go out as a WhatsApp voice note. */
	local_voice?: boolean;
	/** A GIF picked in the GIF tab: its clip on Konvo's media proxy, fetched and sent as a WhatsApp GIF. */
	local_gif_src?: string;
	/** Sent with the link's preview card (false: the preview was closed in the composer). */
	local_link_preview?: boolean;
}

export type WhatsAppPresenceState = 'available' | 'unavailable' | 'composing' | 'recording';

export interface WhatsAppPresence {
	state: WhatsAppPresenceState;
	/** In a group, who is typing. */
	participant: string | null;
	participant_name: string | null;
	/** Unix seconds, when WhatsApp shares it. */
	last_seen: number | null;
	at: number;
}

interface RemoteUpdate {
	status?: WhatsAppMessage['status'];
	kind?: string;
	jid?: string;
	state?: string;
	message_id?: string;
	from_me?: boolean;
	message?: WhatsAppMessage | null;
	chat?: WhatsAppChat | null;
	participant?: string | null;
	participant_name?: string | null;
	presence?: string;
	last_seen?: number | null;
	/** kind 'receipts': a group member read or got one of our messages. */
	read?: number;
	delivered?: number;
	total?: number | null;
}

const STATUS_TTL_MS = 60_000;
const PAIRING_POLL_MS = 2_000;
const RESYNC_MIN_GAP_MS = 2_500;
/** A chat list this fresh is kept when another view asks for it (gateway events keep it current). */
const CHATS_MIN_GAP_MS = 3_000;
/** How long after a read/unread write a chat list fetched before it may still carry the old count. */
const READ_SETTLE_MS = 5_000;
const READ_RETRY_MS = 1_500;
/** The bridge forgets us after 75 s without a report. */
const ACTIVITY_HEARTBEAT_MS = 30_000;
/** WhatsApp forgets a "composing" after about ten seconds, so repeat while typing. */
const TYPING_REPEAT_MS = 7_000;
const TYPING_PAUSE_MS = 5_000;
const TYPING_EXPIRE_MS = 12_000;
/** A local bubble matches the real message the bridge sends back within this window. */
const LOCAL_MATCH_MS = 120_000;

/** The delivery in flight per chat; the next waits for it (kept out of MobX on purpose). */
const sendQueues = new Map<string, Promise<unknown>>();

/**
 * One delivery at a time per chat. The composer no longer waits for a send to finish,
 * so without this a slow photo upload would land after the text typed below it.
 */
export function inOrder<T>(jid: string, run: () => Promise<T>): Promise<T> {
	const previous = sendQueues.get(jid);
	// Nothing ahead: start now, so the request leaves in the same tick as before.
	const next = previous ? previous.catch(() => undefined).then(run) : run();
	sendQueues.set(jid, next);
	void next
		.catch(() => undefined)
		.finally(() => {
			if (sendQueues.get(jid) === next) sendQueues.delete(jid);
		});
	return next;
}

let localCounter = 0;

/** A read/unread choice that a chat list or update sent before WhatsApp applied it must not undo. */
interface ReadEdit {
	read: boolean;
	/** The chat's last message time when chosen; a later message is new unread mail. */
	lastAt: number;
	/** When the write succeeded plus READ_SETTLE_MS; null while it is in flight. */
	until: number | null;
}

class KonvoWhatsAppStore {
	status: WhatsAppStatus | null = null;
	lastSuccessfulSyncAt: number | null = null;
	syncError = false;
	statusError = false;
	chats: Array<WhatsAppChat> = [];
	messages = new Map<string, Array<WhatsAppMessage>>();
	hasOlderMessages = new Map<string, boolean>();
	unreadOnOpen = new Map<string, number>();
	/** A chat with nothing cached yet: its first page is on its way, or it could not be read. */
	messageLoad = new Map<string, 'loading' | 'failed'>();
	selectedJid: string | null = null;
	loadingChats = false;
	/** Typing and online state per chat, for the one the person has open. */
	presence = new Map<string, WhatsAppPresence>();
	/** How many conversation views are showing each chat right now. */
	private viewers = new Map<string, number>();
	private avatarTried = new Set<string>();
	/** jid → object URL of the picture bytes (null when there is none). */
	avatarImages = new Map<string, string | null>();
	private avatarLoading = new Set<string>();
	private prefetching = new Set<string>();
	private statusAt = 0;
	private statusRequest: Promise<WhatsAppStatus | null> | null = null;
	private chatsLoad = new CoalescedLoad(CHATS_MIN_GAP_MS);
	private readEdits = new Map<string, ReadEdit>();
	private lastSyncAt = 0;
	private pairingTimer: ReturnType<typeof setInterval> | null = null;
	private activityTimer: ReturnType<typeof setInterval> | null = null;
	private presenceTimers = new Map<string, ReturnType<typeof setTimeout>>();
	private typingSentAt = 0;
	private typingJid: string | null = null;
	private typingAction: 'composing' | 'recording' = 'composing';
	private typingPauseTimer: ReturnType<typeof setTimeout> | null = null;
	private readonly logger = new Logger('KonvoWhatsApp');

	constructor() {
		makeAutoObservable<
			this,
			| 'statusAt'
			| 'statusRequest'
			| 'chatsLoad'
			| 'readEdits'
			| 'lastSyncAt'
			| 'pairingTimer'
			| 'activityTimer'
			| 'presenceTimers'
			| 'typingSentAt'
			| 'typingJid'
			| 'typingAction'
			| 'typingPauseTimer'
			| 'logger'
			| 'avatarTried'
			| 'avatarLoading'
			| 'avatarQueue'
			| 'avatarInFlight'
			| 'prefetching'
		>(
			this,
			{
				statusAt: false,
				statusRequest: false,
				chatsLoad: false,
				readEdits: false,
				lastSyncAt: false,
				pairingTimer: false,
				activityTimer: false,
				presenceTimers: false,
				typingSentAt: false,
				typingJid: false,
				typingAction: false,
				typingPauseTimer: false,
				logger: false,
				avatarTried: false,
				avatarLoading: false,
				avatarQueue: false,
				avatarInFlight: false,
				prefetching: false,
			},
			{autoBind: true},
		);
		// WhatsApp Web semantics: we are "on WhatsApp" while a chat is open in a
		// focused, visible window. That is when reads go out, typing streams in
		// and the phone stays quiet. Coming back to the window also catches up
		// on anything the socket missed while the phone was in a pocket.
		reaction(
			() => this.viewingJid,
			(jid) => this.onViewingChanged(jid),
		);
		reaction(
			() => Window.visible && Window.focused,
			(awake) => {
				if (awake) void this.resync();
			},
		);
	}

	get linked(): boolean {
		return this.status?.linked === true;
	}

	get connected(): boolean {
		return this.status?.state === 'connected';
	}

	get unreadCount(): number {
		return this.chats.reduce((sum, chat) => sum + (chat.archived ? 0 : chat.unread), 0);
	}

	/** The chat the person is actually looking at, or null when the window is away. */
	get viewingJid(): string | null {
		if (!Window.visible || !Window.focused) return null;
		const jid = this.selectedJid;
		if (!jid || (this.viewers.get(jid) ?? 0) === 0) return null;
		return jid;
	}

	/** Cheap: only hits the API when the cached status is stale. */
	async ensureStatus(maxAgeMs = STATUS_TTL_MS): Promise<WhatsAppStatus | null> {
		if (this.status && Date.now() - this.statusAt < maxAgeMs) return this.status;
		return await this.refreshStatus();
	}

	/** Callers at the same moment (READY, the Rail, Home, the chat list) share one request. */
	refreshStatus(): Promise<WhatsAppStatus | null> {
		if (!this.statusRequest) {
			this.statusRequest = this.fetchStatus().finally(() => {
				this.statusRequest = null;
			});
		}
		return this.statusRequest;
	}

	private async fetchStatus(): Promise<WhatsAppStatus | null> {
		try {
			const response = await http.get<WhatsAppStatus>(Endpoints.KONVO_WHATSAPP_STATUS);
			runInAction(() => {
				this.status = response.body ?? null;
				this.statusAt = Date.now();
				this.statusError = false;
			});
			if (this.status?.state === 'pairing') this.startPairingPoll();
			else this.stopPairingPoll();
			return this.status;
		} catch (error) {
			runInAction(() => {
				this.statusError = true;
			});
			this.logger.warn('status failed', error);
			return this.status;
		}
	}

	async link(): Promise<void> {
		await http.post(Endpoints.KONVO_WHATSAPP_LINK, {body: {}});
		await this.refreshStatus();
		this.startPairingPoll();
	}

	async unlink(): Promise<void> {
		this.stopPairingPoll();
		await http.delete(Endpoints.KONVO_WHATSAPP_LINK);
		runInAction(() => {
			this.chats = [];
			this.messages.clear();
			this.presence.clear();
			this.selectedJid = null;
		});
		await this.refreshStatus();
	}

	private startPairingPoll(): void {
		if (this.pairingTimer) return;
		this.pairingTimer = setInterval(() => {
			void this.refreshStatus().then((status) => {
				if (status?.state === 'connected') void this.refreshChats(true);
			});
		}, PAIRING_POLL_MS);
	}

	private stopPairingPoll(): void {
		if (this.pairingTimer) clearInterval(this.pairingTimer);
		this.pairingTimer = null;
	}

	/**
	 * The chat list. Callers at the same moment share one request, and a list fetched under
	 * CHATS_MIN_GAP_MS ago is kept (several views ask for it as they mount) unless `force`.
	 */
	refreshChats(force = false): Promise<void> {
		if (!this.linked) return Promise.resolve();
		return this.chatsLoad.run(() => this.fetchChats(), {force});
	}

	private async fetchChats(): Promise<void> {
		this.loadingChats = true;
		try {
			const response = await http.get<{chats: Array<WhatsAppChat>}>(Endpoints.KONVO_WHATSAPP_CHATS);
			runInAction(() => {
				this.chats = (response.body?.chats ?? []).map((chat) => this.withReadEdit(chat));
				this.lastSuccessfulSyncAt = Date.now();
				this.syncError = false;
			});
		} catch (error) {
			runInAction(() => {
				this.syncError = true;
			});
			this.logger.warn('chats failed', error);
		} finally {
			runInAction(() => {
				this.loadingChats = false;
			});
		}
	}

	async loadMessages(jid: string): Promise<void> {
		// Remember the exact entries visible when the request starts. A gateway
		// message can arrive before this response; the response is then an older
		// snapshot and must not erase that live update.
		const requestSnapshot = new Map((this.messages.get(jid) ?? []).map((entry) => [entry.id, entry]));
		if (!this.messages.has(jid)) runInAction(() => this.messageLoad.set(jid, 'loading'));
		try {
			const response = await http.get<{messages: Array<WhatsAppMessage>; has_older?: boolean}>(
				Endpoints.KONVO_WHATSAPP_MESSAGES(jid),
			);
			runInAction(() => {
				this.hasOlderMessages.set(jid, response.body?.has_older === true);
				const fresh = response.body?.messages ?? [];
				const current = this.messages.get(jid) ?? [];
				const currentById = new Map(current.map((entry) => [entry.id, entry]));
				const list = fresh.map((message) => ({
					...(currentById.get(message.id) && requestSnapshot.get(message.id) !== currentById.get(message.id)
						? currentById.get(message.id)!
						: message),
					status: advancedStatus(currentById.get(message.id)?.status ?? null, message.status),
				}));
				for (const entry of current) {
					const changedWhileLoading = requestSnapshot.get(entry.id) !== entry;
					const alreadyIncluded = list.some((candidate) => candidate.id === entry.id || matchesLocal(candidate, entry));
					if ((entry.local || changedWhileLoading) && !alreadyIncluded) {
						list.push(entry);
					}
				}
				this.messages.set(jid, sortMessages(list));
				this.messageLoad.delete(jid);
			});
		} catch (error) {
			runInAction(() => {
				if (!this.messages.has(jid)) this.messageLoad.set(jid, 'failed');
				else this.messageLoad.delete(jid);
			});
			this.logger.warn('messages failed', error);
		}
	}

	/**
	 * Catch up after the socket was away (a resumed gateway, a phone coming
	 * back from the pocket): the chat list and the open conversation.
	 */
	async resync(): Promise<void> {
		if (!this.linked) return;
		const now = Date.now();
		if (now - this.lastSyncAt < RESYNC_MIN_GAP_MS) return;
		this.lastSyncAt = now;
		await this.refreshChats();
		const jid = this.selectedJid;
		if (jid) await this.loadMessages(jid);
	}

	/** Warm a chat somebody is about to open. Nothing to do when it is cached or already on its way. */
	prefetchMessages(jid: string): void {
		if (this.messages.has(jid) || this.prefetching.has(jid)) return;
		this.prefetching.add(jid);
		void this.loadMessages(jid).finally(() => this.prefetching.delete(jid));
	}

	select(jid: string | null): void {
		if (jid && jid !== this.selectedJid)
			this.unreadOnOpen.set(jid, this.chats.find((chat) => chat.jid === jid)?.unread ?? 0);
		this.selectedJid = jid;
		if (!jid) return;
		void this.loadMessages(jid);
		const chat = this.chats.find((entry) => entry.jid === jid);
		if (chat && !chat.avatar_url && chat.has_picture) void this.ensureAvatar(jid);
	}

	/** A conversation view for this chat is on screen. */
	attachViewer(jid: string): void {
		this.viewers.set(jid, (this.viewers.get(jid) ?? 0) + 1);
	}

	detachViewer(jid: string): void {
		const next = (this.viewers.get(jid) ?? 1) - 1;
		if (next <= 0) this.viewers.delete(jid);
		else this.viewers.set(jid, next);
	}

	private onViewingChanged(jid: string | null): void {
		if (jid) {
			void this.reportActivity(true);
			void this.subscribePresence(jid);
			const chat = this.chats.find((entry) => entry.jid === jid);
			if (chat && chat.unread > 0) void this.markRead(jid);
			if (!this.activityTimer) {
				this.activityTimer = setInterval(() => void this.reportActivity(true), ACTIVITY_HEARTBEAT_MS);
			}
			return;
		}
		if (this.activityTimer) clearInterval(this.activityTimer);
		this.activityTimer = null;
		this.stopTyping();
		void this.reportActivity(false);
	}

	private async reportActivity(active: boolean): Promise<void> {
		if (!this.linked) return;
		await http.post(Endpoints.KONVO_WHATSAPP_ACTIVITY, {body: {active}, mode: 'silent'}).catch(() => {});
	}

	private async subscribePresence(jid: string): Promise<void> {
		if (!this.linked) return;
		await http
			.post(Endpoints.KONVO_WHATSAPP_PRESENCE(jid), {body: {action: 'subscribe'}, mode: 'silent'})
			.catch(() => {});
	}

	/**
	 * Called on every keystroke in the composer (and every tick while recording);
	 * tells WhatsApp "typing…" or "recording audio…" the way the phone does.
	 */
	noteTyping(jid: string, action: 'composing' | 'recording' = 'composing'): void {
		if (this.viewingJid !== jid) return;
		const now = Date.now();
		if (this.typingJid !== jid || this.typingAction !== action || now - this.typingSentAt > TYPING_REPEAT_MS) {
			this.typingJid = jid;
			this.typingAction = action;
			this.typingSentAt = now;
			void http.post(Endpoints.KONVO_WHATSAPP_PRESENCE(jid), {body: {action}, mode: 'silent'}).catch(() => {});
		}
		if (this.typingPauseTimer) clearTimeout(this.typingPauseTimer);
		this.typingPauseTimer = setTimeout(() => this.stopTyping(), TYPING_PAUSE_MS);
	}

	stopTyping(): void {
		if (this.typingPauseTimer) clearTimeout(this.typingPauseTimer);
		this.typingPauseTimer = null;
		const jid = this.typingJid;
		if (!jid) return;
		this.typingJid = null;
		this.typingSentAt = 0;
		void http.post(Endpoints.KONVO_WHATSAPP_PRESENCE(jid), {body: {action: 'paused'}, mode: 'silent'}).catch(() => {});
	}

	private avatarQueue: Array<string> = [];
	private avatarInFlight = 0;

	/**
	 * The picture bytes come through the API (the browser cannot load WhatsApp's
	 * CDN). Fetched a few at a time so a fresh load of a long list never trips
	 * the API rate limit, which would also block the messages themselves.
	 */
	loadAvatarImage(jid: string): void {
		if (this.avatarImages.has(jid) || this.avatarLoading.has(jid)) return;
		this.avatarLoading.add(jid);
		this.avatarQueue.push(jid);
		this.pumpAvatars();
	}

	private pumpAvatars(): void {
		while (this.avatarInFlight < 2 && this.avatarQueue.length > 0) {
			const jid = this.avatarQueue.shift();
			if (!jid) break;
			this.avatarInFlight += 1;
			void http
				.get<Blob>(Endpoints.KONVO_WHATSAPP_AVATAR_IMAGE(jid), {parse: 'binary'})
				.then((response) => {
					const blob = response.body instanceof Blob ? response.body : null;
					runInAction(() => {
						this.avatarImages.set(jid, blob ? URL.createObjectURL(blob) : null);
					});
				})
				.catch((error: {status?: number}) => {
					// Rate limited: forget it for now so it is retried next time it renders.
					runInAction(() => {
						if (error?.status !== 429) this.avatarImages.set(jid, null);
					});
				})
				.finally(() => {
					this.avatarLoading.delete(jid);
					this.avatarInFlight -= 1;
					setTimeout(() => this.pumpAvatars(), 120);
				});
		}
	}

	/** Pictures for the busiest chats come with the list; the rest are fetched when opened. */
	async ensureAvatar(jid: string): Promise<void> {
		if (this.avatarTried.has(jid)) return;
		this.avatarTried.add(jid);
		try {
			const response = await http.get<{url: string | null}>(Endpoints.KONVO_WHATSAPP_AVATAR(jid));
			const url = response.body?.url ?? null;
			if (!url) return;
			runInAction(() => {
				const chat = this.chats.find((entry) => entry.jid === jid);
				if (chat) chat.avatar_url = url;
			});
		} catch {
			// no picture, initials stay
		}
	}

	/** Read here and on the phone. Tried twice; if WhatsApp still refuses, the list shows its real state again. */
	async markRead(jid: string): Promise<void> {
		const edit = this.beginReadEdit(jid, true);
		runInAction(() => {
			const chat = this.chats.find((entry) => entry.jid === jid);
			if (chat) chat.unread = 0;
		});
		for (let attempt = 1; ; attempt++) {
			try {
				await http.post(Endpoints.KONVO_WHATSAPP_READ(jid), {body: {}});
				this.settleReadEdit(jid, edit);
				return;
			} catch (error) {
				if (this.readEdits.get(jid) !== edit) return;
				if (attempt < 2) {
					await new Promise((resolve) => setTimeout(resolve, READ_RETRY_MS));
					if (this.readEdits.get(jid) !== edit) return;
					continue;
				}
				this.logger.warn('mark read failed', error);
				this.readEdits.delete(jid);
				void this.refreshChats(true);
				return;
			}
		}
	}

	/** WhatsApp's "Mark as unread": an unread dot here and on the phone until the chat is opened again. */
	async markUnread(jid: string): Promise<void> {
		const chat = this.chats.find((entry) => entry.jid === jid);
		const before = chat?.unread ?? 0;
		const edit = this.beginReadEdit(jid, false);
		runInAction(() => {
			if (chat) chat.unread = Math.max(before, 1);
		});
		try {
			await this.updateChatSettings(jid, 'unread', true);
			this.settleReadEdit(jid, edit);
		} catch (error) {
			if (this.readEdits.get(jid) === edit) {
				this.readEdits.delete(jid);
				runInAction(() => {
					if (chat) chat.unread = before;
				});
			}
			throw error;
		}
	}

	private beginReadEdit(jid: string, read: boolean): ReadEdit {
		const lastAt = this.chats.find((entry) => entry.jid === jid)?.last_at;
		const edit: ReadEdit = {read, lastAt: lastAt ? Date.parse(lastAt) : 0, until: null};
		this.readEdits.set(jid, edit);
		return edit;
	}

	private settleReadEdit(jid: string, edit: ReadEdit): void {
		if (this.readEdits.get(jid) === edit) edit.until = Date.now() + READ_SETTLE_MS;
	}

	/** The read state the person chose wins over a chat fetched before WhatsApp applied it. */
	private withReadEdit(chat: WhatsAppChat): WhatsAppChat {
		const edit = this.readEdits.get(chat.jid);
		if (!edit) return chat;
		const agrees = edit.read ? chat.unread === 0 : chat.unread > 0;
		if (edit.until !== null && (agrees || Date.now() > edit.until)) {
			this.readEdits.delete(chat.jid);
			return chat;
		}
		if (agrees) return chat;
		// Someone wrote after it was read: that message is unread.
		if (edit.read) return Date.parse(chat.last_at ?? '') > edit.lastAt ? chat : {...chat, unread: 0};
		return {...chat, unread: 1};
	}

	/** A photo, video or file from the composer (paste, drop or the paperclip). */
	async sendMedia(jid: string, file: File, caption = '', replyTo: string | null = null): Promise<boolean> {
		// A GIF goes out as WhatsApp's own GIF (the API makes it a looping clip), so it shows as one at once.
		const gif = file.type === 'image/gif';
		const kind = gif ? 'video' : file.type.startsWith('image/') ? 'image' : mediaKindOf(file.type);
		const local = this.addLocal(jid, {
			text: caption,
			media_kind: kind,
			media_gif: gif,
			media_name: kind === 'document' ? file.name : null,
			local_url: kind === 'image' || gif ? URL.createObjectURL(file) : null,
			local_file: file,
			...quoteFields(this.messages.get(jid), replyTo),
		});
		return inOrder(jid, () => this.deliverMedia(jid, local));
	}

	/**
	 * A GIF from the composer's GIF tab. It shows at once from Konvo's media proxy; then its clip is
	 * fetched and the API sends it as a WhatsApp GIF.
	 */
	async sendGif(jid: string, gif: Gif, replyTo: string | null = null): Promise<boolean> {
		const source = whatsAppGifSource(gif);
		const local = this.addLocal(jid, {
			media_kind: 'video',
			media_gif: true,
			media_width: source.width || gif.width || null,
			media_height: source.height || gif.height || null,
			local_url: source.video ? source.src : null,
			local_gif_src: source.src,
			...quoteFields(this.messages.get(jid), replyTo),
		});
		return inOrder(jid, () => this.deliverGif(jid, local));
	}

	private async deliverGif(jid: string, local: WhatsAppMessage): Promise<boolean> {
		const src = local.local_gif_src;
		const blob = src
			? await fetch(src)
					.then((response) => (response.ok ? response.blob() : null))
					.catch(() => null)
			: null;
		if (!blob) {
			this.failLocal(jid, local.id);
			return false;
		}
		const type = blob.type || 'video/mp4';
		const file = new File([blob], type === 'image/gif' ? 'gif.gif' : 'gif.mp4', {type});
		return this.deliverMedia(jid, {...local, local_file: file});
	}

	/**
	 * A recording from the composer mic. It shows as a voice note at once (the
	 * recording plays from memory, with the waveform sampled while recording) and
	 * the API sends it to WhatsApp as a real voice note.
	 */
	async sendVoiceNote(
		jid: string,
		file: File,
		voice: {seconds: number; waveform: Array<number>},
		replyTo: string | null = null,
	): Promise<boolean> {
		this.stopTyping();
		const local = this.addLocal(jid, {
			media_kind: 'audio',
			media_mime: file.type || null,
			local_url: URL.createObjectURL(file),
			local_file: file,
			local_voice: true,
			voice_seconds: Math.max(1, Math.round(voice.seconds)),
			voice_waveform: voice.waveform,
			...quoteFields(this.messages.get(jid), replyTo),
		});
		return inOrder(jid, () => this.deliverMedia(jid, local));
	}

	private async deliverMedia(jid: string, local: WhatsAppMessage): Promise<boolean> {
		const file = local.local_file;
		if (!file) return false;
		const caption = local.text;
		const replyTo = local.reply_to;
		try {
			const form = new FormData();
			form.append('file', file, file.name || 'upload');
			if (local.send_id) form.append('send_id', local.send_id);
			if (caption) form.append('caption', caption);
			if (replyTo) form.append('reply_to', replyTo);
			if (local.local_voice) form.append('voice', '1');
			if (local.media_gif) form.append('gif', '1');
			const response = await http.post<{message?: WhatsAppMessage | null; message_id?: string | null}>(
				Endpoints.KONVO_WHATSAPP_SEND_MEDIA(jid),
				{body: form, timeoutMs: 180_000},
			);
			this.settleLocal(jid, local.id, response.body?.message ?? null, response.body?.message_id ?? null);
			return true;
		} catch (error) {
			if (this.hasConfirmedSend(jid, local)) return true;
			this.logger.warn('media send failed', error);
			this.failLocal(jid, local.id);
			return false;
		}
	}

	async react(jid: string, messageId: string, emoji: string): Promise<void> {
		// Optimistic: show my reaction at once; the bridge event confirms it.
		runInAction(() => {
			const message = this.messages.get(jid)?.find((entry) => entry.id === messageId);
			if (message) {
				message.reactions = message.reactions.filter((reaction) => !reaction.from_me);
				if (emoji) message.reactions.push({emoji, from_me: true, sender_name: null});
			}
		});
		await http.post(Endpoints.KONVO_WHATSAPP_REACT(jid), {body: {message_id: messageId, emoji}}).catch(() => {});
	}

	/** Ask WhatsApp to delete one of our sent messages for every participant. */
	/** Message info: who has read and received one of our messages (per person in groups). */
	async fetchReceipts(jid: string, messageId: string): Promise<WhatsAppReceipts> {
		const response = await http.get<WhatsAppReceipts>(Endpoints.KONVO_WHATSAPP_MESSAGE_RECEIPTS(jid, messageId));
		return response.body;
	}

	async deleteMessage(jid: string, messageId: string): Promise<void> {
		const response = await http.delete<{message?: WhatsAppMessage | null}>(
			Endpoints.KONVO_WHATSAPP_MESSAGE(jid, messageId),
		);
		if (response.body?.message) this.upsertMessage(jid, response.body.message);
	}

	/**
	 * Change the text (or caption) of one of our sent messages. The bubble shows
	 * the new text at once; the API applies it on WhatsApp and the gateway event
	 * updates every other window. On failure the old text comes back and the
	 * error (with the API's `code`, e.g. `too_old`) is rethrown for the caller.
	 */
	async editMessage(jid: string, messageId: string, text: string): Promise<void> {
		const next = text.trim();
		const message = this.messages.get(jid)?.find((entry) => entry.id === messageId);
		const before = message ? {text: message.text, edited: message.edited} : null;
		if (message && next) {
			runInAction(() => {
				message.text = next;
				message.edited = true;
				this.messages.set(jid, [...(this.messages.get(jid) ?? [])]);
			});
		}
		try {
			const response = await http.patch<{message?: WhatsAppMessage | null}>(
				Endpoints.KONVO_WHATSAPP_MESSAGE(jid, messageId),
				{body: {text: next}},
			);
			if (response.body?.message) this.upsertMessage(jid, response.body.message);
		} catch (error) {
			const current = this.messages.get(jid)?.find((entry) => entry.id === messageId);
			if (current && before && current.text === next) {
				runInAction(() => {
					current.text = before.text;
					current.edited = before.edited;
					this.messages.set(jid, [...(this.messages.get(jid) ?? [])]);
				});
			}
			throw error;
		}
	}

	async sendPoll(jid: string, input: WhatsAppPollInput): Promise<boolean> {
		this.stopTyping();
		const local = this.addLocal(jid, {
			text: input.question,
			media_kind: 'poll',
			local_poll: input,
			poll: {
				question: input.question,
				selectable_count: input.multiple ? input.options.length : 1,
				can_vote: false,
				total_voters: 0,
				options: input.options.map((name) => ({name, selected: false, voters: []})),
			},
		});
		return inOrder(jid, () => this.deliverPoll(jid, local));
	}

	private async deliverPoll(jid: string, local: WhatsAppMessage): Promise<boolean> {
		try {
			const response = await http.post<{message?: WhatsAppMessage | null; message_id?: string | null}>(
				Endpoints.KONVO_WHATSAPP_POLLS(jid),
				{body: {poll: local.local_poll, send_id: local.send_id}, timeoutMs: 90_000},
			);
			this.settleLocal(jid, local.id, response.body?.message ?? null, response.body?.message_id ?? null);
			return true;
		} catch (error) {
			if (this.hasConfirmedSend(jid, local)) return true;
			this.logger.warn('poll send failed', error);
			this.failLocal(jid, local.id);
			return false;
		}
	}

	async sendLocation(jid: string, input: WhatsAppLocationInput): Promise<boolean> {
		this.stopTyping();
		const local = this.addLocal(jid, {
			text: input.name ?? input.address ?? '',
			media_kind: 'location',
			local_location: input,
			location: {...input, url: null, live: false, has_thumbnail: false},
		});
		return inOrder(jid, () => this.deliverShare(jid, local));
	}

	async sendContacts(jid: string, input: Array<WhatsAppContactInput>): Promise<boolean> {
		this.stopTyping();
		const local = this.addLocal(jid, {
			text: input.length === 1 ? input[0].name : `${input.length} contacts`,
			media_kind: 'contact',
			local_contacts: input,
			contacts: input.map((contact) => ({
				name: contact.name,
				phones: [{number: `+${contact.phone}`, jid: `${contact.phone}@s.whatsapp.net`}],
			})),
		});
		return inOrder(jid, () => this.deliverShare(jid, local));
	}

	private async deliverShare(jid: string, local: WhatsAppMessage): Promise<boolean> {
		try {
			const response = await http.post<{message?: WhatsAppMessage | null; message_id?: string | null}>(
				local.local_location
					? Endpoints.KONVO_WHATSAPP_SEND_LOCATION(jid)
					: Endpoints.KONVO_WHATSAPP_SEND_CONTACTS(jid),
				{
					body: local.local_location
						? {location: local.local_location, send_id: local.send_id}
						: {contacts: local.local_contacts, send_id: local.send_id},
					timeoutMs: 90_000,
				},
			);
			this.settleLocal(jid, local.id, response.body?.message ?? null, response.body?.message_id ?? null);
			return true;
		} catch (error) {
			if (this.hasConfirmedSend(jid, local)) return true;
			this.logger.warn('share send failed', error);
			this.failLocal(jid, local.id);
			return false;
		}
	}

	/** People in the phone's address book, to share as contact cards. */
	async loadAddressBook(): Promise<Array<WhatsAppAddressBookEntry>> {
		const response = await http.get<{contacts?: Array<WhatsAppAddressBookEntry>}>(Endpoints.KONVO_WHATSAPP_CONTACTS);
		return Array.isArray(response.body?.contacts) ? response.body.contacts : [];
	}

	async votePoll(jid: string, messageId: string, options: Array<number>, sendId: string): Promise<void> {
		const response = await http.post<{message?: WhatsAppMessage | null}>(
			Endpoints.KONVO_WHATSAPP_VOTE(jid, messageId),
			{body: {options, send_id: sendId}, timeoutMs: 90_000},
		);
		if (response.body?.message) this.upsertMessage(jid, response.body.message);
	}

	/** linkPreview false: the person closed the link's preview, so WhatsApp gets none. */
	async send(jid: string, text: string, replyTo: string | null = null, linkPreview = true): Promise<boolean> {
		this.stopTyping();
		const local = this.addLocal(jid, {
			text,
			...quoteFields(this.messages.get(jid), replyTo),
			...(linkPreview ? {} : {local_link_preview: false}),
		});
		return await inOrder(jid, () => this.deliver(jid, local, replyTo));
	}

	/** Try a failed bubble again, in place. */
	async retry(jid: string, localId: string): Promise<boolean> {
		const local = this.messages.get(jid)?.find((entry) => entry.id === localId);
		if (!local?.local || local.status !== 'failed') return false;
		runInAction(() => {
			local.status = 'pending';
			local.sent_at = new Date().toISOString();
		});
		if (local.local_poll) return inOrder(jid, () => this.deliverPoll(jid, local));
		if (local.local_location || local.local_contacts) return inOrder(jid, () => this.deliverShare(jid, local));
		if (local.local_gif_src) return inOrder(jid, () => this.deliverGif(jid, local));
		if (local.local_file) return inOrder(jid, () => this.deliverMedia(jid, local));
		return await inOrder(jid, () => this.deliver(jid, local, local.reply_to));
	}

	private async deliver(jid: string, local: WhatsAppMessage, replyTo: string | null): Promise<boolean> {
		try {
			const response = await http.post<{message?: WhatsAppMessage | null; message_id?: string | null}>(
				Endpoints.KONVO_WHATSAPP_MESSAGES(jid),
				{
					body: {
						text: local.text,
						reply_to: replyTo,
						send_id: local.send_id,
						link_preview: local.local_link_preview !== false,
					},
				},
			);
			this.settleLocal(jid, local.id, response.body?.message ?? null, response.body?.message_id ?? null);
			return true;
		} catch (error) {
			if (this.hasConfirmedSend(jid, local)) return true;
			this.logger.warn('send failed', error);
			this.failLocal(jid, local.id);
			return false;
		}
	}

	private addLocal(jid: string, fields: Partial<WhatsAppMessage>): WhatsAppMessage {
		localCounter += 1;
		const now = new Date().toISOString();
		const local: WhatsAppMessage = {
			id: `local:${Date.now()}:${localCounter}`,
			send_id: `3EB0${generateUuid().replace(/-/g, '').toUpperCase()}`,
			jid,
			from_me: true,
			sender_jid: null,
			sender_name: null,
			text: '',
			media_kind: null,
			media_name: null,
			media_width: null,
			media_height: null,
			status: 'pending',
			sent_at: now,
			reply_to: null,
			quote_text: null,
			quote_sender: null,
			reactions: [],
			mentions: {},
			sender_has_picture: false,
			sender_avatar_jid: null,
			local: true,
			...fields,
		};
		runInAction(() => {
			const list = this.messages.get(jid) ?? [];
			this.messages.set(jid, [...list, local]);
			this.touchChat(jid, local);
		});
		return local;
	}

	/** The bridge answered: swap the local bubble for the real message (or just its id). */
	private settleLocal(jid: string, localId: string, message: WhatsAppMessage | null, messageId: string | null): void {
		runInAction(() => {
			const list = this.messages.get(jid);
			if (!list) return;
			const index = list.findIndex((entry) => entry.id === localId);
			if (index === -1) return;
			const local = list[index];
			if (message) {
				// The gateway event may have landed first; never show the message twice.
				if (list.some((entry) => entry.id === message.id && entry.id !== localId)) {
					this.dropLocal(local);
					list.splice(index, 1);
					this.messages.set(jid, [...list]);
					return;
				}
				this.dropLocal(local);
				list[index] = {
					...message,
					...voiceFallback(message, local),
					status: confirmedStatus(local.status, message.status),
					local_url: local.local_url ?? null,
				};
			} else if (messageId) {
				list[index] = {...local, id: messageId, local: false, status: confirmedStatus(local.status, null)};
			} else {
				list[index] = {...local, status: 'failed'};
			}
			this.tickChat(jid, local.sent_at, list[index].status);
			this.messages.set(jid, sortMessages(list));
		});
	}

	private hasConfirmedSend(jid: string, local: WhatsAppMessage): boolean {
		return Boolean(
			local.send_id && this.messages.get(jid)?.some((entry) => entry.id === local.send_id && !entry.local),
		);
	}

	private failLocal(jid: string, localId: string): void {
		runInAction(() => {
			const local = this.messages.get(jid)?.find((entry) => entry.id === localId);
			if (!local) return;
			local.status = 'failed';
			this.tickChat(jid, local.sent_at, 'failed');
		});
	}

	/**
	 * A message still only known here changed status: its chat row's ticks follow while it is the
	 * row's newest message. Once the bridge's chat arrives, that is the row's answer instead.
	 */
	private tickChat(jid: string, sentAt: string, status: WhatsAppMessageStatus | null): void {
		const chat = this.chats.find((entry) => entry.jid === jid);
		if (!chat?.last_from_me || chat.last_at !== sentAt || !status) return;
		chat.last_status = status === 'failed' ? status : advancedStatus(chat.last_status ?? null, status);
	}

	private dropLocal(message: WhatsAppMessage): void {
		// Keep the preview URL while the real photo still has to download; the
		// bubble revokes it when it swaps in the bytes from WhatsApp.
		message.local_file = null;
	}

	/** Insert or replace one message; returns true when it was not there before. */
	upsertMessage(jid: string, message: WhatsAppMessage): boolean {
		let fresh = false;
		runInAction(() => {
			const list = this.messages.get(jid);
			if (!list) {
				fresh = true;
				this.messages.set(jid, [message]);
				return;
			}
			const index = list.findIndex((entry) => entry.id === message.id);
			if (index !== -1) {
				list[index] = {
					...message,
					status: advancedStatus(list[index].status, message.status),
					local_url: list[index].local_url ?? null,
				};
				this.messages.set(jid, [...list]);
				return;
			}
			const localIndex = message.from_me ? list.findIndex((entry) => entry.local && matchesLocal(message, entry)) : -1;
			if (localIndex !== -1) {
				const local = list[localIndex];
				this.dropLocal(local);
				list[localIndex] = {
					...message,
					...voiceFallback(message, local),
					status: confirmedStatus(local.status, message.status),
					local_url: local.local_url ?? null,
				};
				this.messages.set(jid, sortMessages(list));
				return;
			}
			fresh = true;
			this.messages.set(jid, sortMessages([...list, message]));
		});
		return fresh;
	}

	async updateChatSettings(
		jid: string,
		operation: 'pin' | 'mute' | 'archive' | 'unread',
		value: boolean,
	): Promise<void> {
		const response = await http.post<{chat: WhatsAppChat}>(Endpoints.KONVO_WHATSAPP_CHAT_SETTINGS(jid), {
			body: {operation, value},
		});
		if (!response.body?.chat) throw new Error('Chat update failed');
		this.upsertChat(response.body.chat);
	}

	upsertChat(update: WhatsAppChat): void {
		runInAction(() => {
			const chat = this.withReadEdit(update);
			const index = this.chats.findIndex((entry) => entry.jid === chat.jid);

			if (index === -1) this.chats.push(chat);
			else Object.assign(this.chats[index], chat);
			this.chats.sort((a, b) => Date.parse(b.last_at ?? '') - Date.parse(a.last_at ?? '') || 0);
		});
	}

	/** An outgoing message moves its chat to the top with a "You: …" preview. */
	private touchChat(jid: string, message: WhatsAppMessage): void {
		const chat = this.chats.find((entry) => entry.jid === jid);
		if (!chat) return;
		chat.last_at = message.sent_at;
		const kind = message.media_kind && PREVIEW_ICON_KINDS.has(message.media_kind) ? message.media_kind : null;
		const body =
			message.text ||
			(kind === 'audio'
				? voicePreviewLabel(message.voice_seconds)
				: mediaLabel(message.media_kind, message.media_name, message.media_gif));
		chat.last_preview = `You: ${body}`.slice(0, 120);
		chat.last_kind = kind;
		chat.last_sender = kind ? 'You' : null;
		chat.last_seconds = kind === 'audio' ? (message.voice_seconds ?? null) : null;
		chat.last_from_me = true;
		chat.last_status = message.status ?? 'pending';
		this.chats.sort((a, b) => Date.parse(b.last_at ?? '') - Date.parse(a.last_at ?? '') || 0);
	}

	/** A gateway event from the bridge: something changed for this account. */
	applyRemote(data: RemoteUpdate): void {
		if (data.kind === 'status') {
			void this.refreshStatus().then(() => {
				if (this.linked) void this.refreshChats(true);
			});
			return;
		}
		if (!this.linked) return;
		const jid = data.jid;
		if (data.kind === 'receipt' && jid && data.message_id && data.status) {
			// Receipts can beat the send HTTP response. While that request is still
			// in flight the bubble has a local UI id, but its reserved send_id is
			// already the real WhatsApp id carried by the receipt.
			const message = this.messages
				.get(jid)
				?.find((entry) => entry.id === data.message_id || entry.send_id === data.message_id);
			if (message?.from_me) message.status = advancedStatus(message.status, data.status);
			// Our newest message in the chat: the bridge sends the row with its new ticks.
			if (data.chat) this.upsertChat(data.chat);
			return;
		}
		if (data.kind === 'receipts' && jid && data.message_id && typeof data.read === 'number') {
			const message = this.messages
				.get(jid)
				?.find((entry) => entry.id === data.message_id || entry.send_id === data.message_id);
			if (message?.from_me)
				message.receipts = {read: data.read, delivered: data.delivered ?? data.read, total: data.total ?? null};
			return;
		}
		if (data.kind === 'presence' && jid) {
			this.applyPresence(jid, data);
			return;
		}
		if (data.kind === 'chat' && data.chat) {
			this.upsertChat(data.chat);
			return;
		}
		if (data.kind === 'message' && jid && data.message && data.chat) {
			const fresh = this.upsertMessage(jid, data.message);
			this.upsertChat(data.chat);
			if (!data.message.from_me) {
				// Someone else's message: their typing bubble is done.
				this.clearTyping(jid, data.message.sender_jid);
				if (this.viewingJid === jid) void this.markRead(jid);
				else if (fresh) this.maybeNotify(data.chat, data.message);
			}
			return;
		}
		// Older bridge or a full sync: fall back to reloading.
		void this.refreshChats(true);
		if (data.kind === 'message' && jid) {
			if (this.messages.has(jid)) void this.loadMessages(jid);
			if (this.viewingJid === jid) void this.markRead(jid);
		} else if (data.kind === 'sync' && this.selectedJid) {
			void this.loadMessages(this.selectedJid);
		}
	}

	private applyPresence(jid: string, data: RemoteUpdate): void {
		const raw = data.presence;
		const state: WhatsAppPresenceState =
			raw === 'composing' || raw === 'recording' || raw === 'available' ? raw : 'unavailable';
		const participant = data.participant ?? null;
		runInAction(() => {
			const current = this.presence.get(jid);
			// A group's "online" is nobody in particular; only typing matters there.
			const group = this.chats.find((entry) => entry.jid === jid)?.is_group === true;
			if (group && state !== 'composing' && state !== 'recording') {
				if (current && current.participant === participant) this.presence.delete(jid);
				return;
			}
			this.presence.set(jid, {
				state,
				participant,
				participant_name: data.participant_name ?? current?.participant_name ?? null,
				last_seen: data.last_seen ?? (state === 'unavailable' ? (current?.last_seen ?? null) : null),
				at: Date.now(),
			});
		});
		const timer = this.presenceTimers.get(jid);
		if (timer) clearTimeout(timer);
		if (state === 'composing' || state === 'recording') {
			this.presenceTimers.set(
				jid,
				setTimeout(() => this.clearTyping(jid, participant), TYPING_EXPIRE_MS),
			);
		}
	}

	private clearTyping(jid: string, participant: string | null): void {
		runInAction(() => {
			const current = this.presence.get(jid);
			if (!current || (current.state !== 'composing' && current.state !== 'recording')) return;
			if (participant && current.participant && current.participant !== participant) return;
			// Someone who was typing a moment ago is still online; a group has no "online".
			if (this.chats.find((entry) => entry.jid === jid)?.is_group) this.presence.delete(jid);
			else this.presence.set(jid, {...current, state: 'available', at: Date.now()});
		});
	}

	/**
	 * Desktop notification for an incoming WhatsApp message, with the same
	 * manners as Konvo DMs: not on Do Not Disturb, not while you are looking
	 * at that chat, and only from the main window (the Rail shares the account).
	 */
	private maybeNotify(chat: WhatsAppChat, message: WhatsAppMessage): void {
		if (window.location.pathname === Routes.RAIL) return;
		if (
			!KonvoNotificationPrefs.whatsapp ||
			chat.archived ||
			(chat.mute_until && (chat.mute_until < 0 || chat.mute_until > Date.now() / 1000))
		)
			return;
		if (LocalPresence.getStatus() === StatusTypes.DND) return;
		if (chat.unread === 0) return;
		// KONVO: Konvo is in front: an in-app pop-up (no OS permission needed) instead, with the same sound.
		// Each part follows WhatsApp's row in the desktop Notifications table (pop-up, this computer, sound).
		const sound = KonvoNotificationPrefs.allowsChannel('whatsapp', 'sound');
		const focused =
			NotificationState.focused || (typeof document !== 'undefined' && !document.hidden && document.hasFocus());
		if (
			KonvoNotificationPrefs.allowsChannel('whatsapp', 'popup') &&
			offerInAppPopup({kind: 'whatsapp', chat, message, focused}) !== 'declined'
		) {
			if (sound) NotificationUtils.playNotificationSoundIfEnabled();
			return;
		}
		if (!NotificationState.browserNotificationsEnabled) return;
		// This computer off: the sound alone, if that is on.
		if (!KonvoNotificationPrefs.allowsChannel('whatsapp', 'desktop')) {
			if (sound) NotificationUtils.playNotificationSoundIfEnabled();
			return;
		}
		const body = message.text || mediaLabel(message.media_kind, message.media_name, message.media_gif);
		void NotificationUtils.showNotification({
			id: message.id,
			title: `WhatsApp · ${chat.name}`,
			body: chat.is_group && message.sender_name ? `${message.sender_name}: ${body}` : body,
			url: Routes.dmWhatsAppChat(chat.jid),
			playSound: sound,
		}).catch(() => {});
	}
}

function advancedStatus(
	existing: WhatsAppMessage['status'],
	incoming: WhatsAppMessage['status'],
): WhatsAppMessage['status'] {
	const rank = {pending: 0, failed: 0, sent: 1, delivered: 2, read: 3};
	return existing && rank[existing] > (incoming ? rank[incoming] : -1) ? existing : incoming;
}

/** A real echo or successful send response means WhatsApp accepted the message. */
function confirmedStatus(
	existing: WhatsAppMessage['status'],
	incoming: WhatsAppMessage['status'],
): WhatsAppMessage['status'] {
	return advancedStatus(advancedStatus(existing, incoming), 'sent');
}

function sortMessages(list: Array<WhatsAppMessage>): Array<WhatsAppMessage> {
	return [...list].sort((a, b) => Date.parse(a.sent_at) - Date.parse(b.sent_at));
}

/** Until the sent voice note carries its own, keep the length and waveform we recorded. */
function voiceFallback(
	real: WhatsAppMessage,
	local: WhatsAppMessage,
): Pick<WhatsAppMessage, 'voice_seconds' | 'voice_waveform'> {
	return {
		voice_seconds: real.voice_seconds ?? local.voice_seconds ?? null,
		voice_waveform: real.voice_waveform ?? local.voice_waveform ?? null,
	};
}

/** The real message the bridge sent back for one of our local bubbles. */
function matchesLocal(real: WhatsAppMessage, local: WhatsAppMessage): boolean {
	if (local.send_id && real.id.startsWith('3EB0')) return real.from_me && real.id === local.send_id;
	if (!real.from_me || real.media_kind !== local.media_kind || real.text !== local.text) return false;
	return Math.abs(Date.parse(real.sent_at) - Date.parse(local.sent_at)) < LOCAL_MATCH_MS;
}

function quoteFields(
	list: Array<WhatsAppMessage> | undefined,
	replyTo: string | null,
): Pick<WhatsAppMessage, 'reply_to' | 'quote_text' | 'quote_sender'> {
	if (!replyTo) return {reply_to: null, quote_text: null, quote_sender: null};
	const quoted = list?.find((entry) => entry.id === replyTo);
	return {
		reply_to: replyTo,
		quote_text: quoted
			? (quoted.text || mediaLabel(quoted.media_kind, quoted.media_name, quoted.media_gif)).slice(0, 200)
			: null,
		quote_sender: quoted ? (quoted.from_me ? 'You' : quoted.sender_name) : null,
	};
}

/** WhatsApp GIF formats in order of preference: an MP4 loop, else the GIF itself (the API converts it). */
const GIF_CLIP_FORMATS = ['mp4', 'loopedmp4', 'mediummp4', 'tinymp4', 'nanomp4'];

/** The file to send for a GIF-tab GIF, through Konvo's media proxy (production's CSP allows it). */
export function whatsAppGifSource(gif: Gif): {src: string; width: number; height: number; video: boolean} {
	for (const key of GIF_CLIP_FORMATS) {
		const format = gif.media[key];
		if (format?.proxy_src) return {src: format.proxy_src, width: format.width, height: format.height, video: true};
	}
	const still = gif.media['gif'];
	if (still?.proxy_src) return {src: still.proxy_src, width: still.width, height: still.height, video: false};
	return {src: gif.proxy_src, width: gif.width, height: gif.height, video: true};
}

function mediaKindOf(mime: string): string {
	if (mime.startsWith('video/')) return 'video';
	if (mime.startsWith('audio/')) return 'audio';
	return 'document';
}

/** Media the chat list draws an icon for ahead of the preview; the API sets the same kinds. */
const PREVIEW_ICON_KINDS = new Set(['image', 'video', 'audio', 'sticker', 'document', 'poll']);

/** WhatsApp's chat list wording for a voice note: "Voice message (0:15)". */
export function voicePreviewLabel(seconds: number | null | undefined): string {
	if (!seconds) return 'Voice message';
	const whole = Math.round(seconds);
	return `Voice message (${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')})`;
}

export function mediaLabel(kind: string | null, name: string | null, gif = false): string {
	switch (kind) {
		case 'image':
			return 'Photo';
		case 'video':
			return gif ? 'GIF' : 'Video';
		case 'audio':
			return 'Voice note';
		case 'sticker':
			return 'Sticker';
		case 'poll':
			return 'Poll';
		case 'location':
			return 'Location';
		case 'contact':
			return 'Contact';
		case 'document':
			return name ?? 'Document';
		case 'deleted':
			return 'This message was deleted';
		default:
			return '';
	}
}

export default new KonvoWhatsAppStore();
